export enum FundingInvoiceStatus {
  InReview = 'IN_REVIEW',
}

export interface GetGeneratedInvoicesRequestDto {
  startMonth?: number
  startYear?: number
  endMonth?: number
  endYear?: number
  /** Optional client UUID; omit to fetch across all clients. */
  clientId?: string
  /** When true, invoices belonging to test clients are excluded. */
  excludeTestClients?: boolean
}

export interface FundingInvoiceListItemDto {
  id: string
  clientId: string
  clientName: string | null
  billingPeriodStart: string
  period: string
  issueDate: string
  amount: number
  employees: number
  status: FundingInvoiceStatus
  version: number
  isLatest: boolean
  versionCount: number
}

export interface FundingInvoiceListResponseDto {
  data: FundingInvoiceListItemDto[]
  total: number
}

export interface InvoiceSummaryDto {
  id: string
  clientId: string
  billingPeriodStart: string
  period: string
  issueDate: string
  status: FundingInvoiceStatus
  amount: number
  /**
   * Funded-month premiums (the Health Plans tab) in dollars: the Plan Costs
   * line of the Invoice Summary. Absent from responses that predate line
   * categories (EMS-10813); the drawer derives it then.
   */
  planCosts?: number
  /**
   * Net of the Adjustments tab in dollars, negative when credits exceed
   * charges. planCosts + adjustments + fees = amount. Absent from older
   * responses, like `planCosts`.
   */
  adjustments?: number
  employees: number
  version: number
  isLatest: boolean
  versionCount: number
}

export interface PlanCostDto {
  ber: number | null
  invoice: number
}

export interface HealthPlanLineDto {
  lineItemId: string
  employeePublicKey: string
  employeeId: string | null
  employeeName: string
  firstName: string | null
  lastName: string | null
  relationship: string | null
  companyName: string | null
  companyCode: string | null
  divisionCode: string | null
  department: string | null
  departmentCode: string | null
  employmentStatus: string | null
  carrierName: string | null
  benefitId: string
  benefitEffectiveDate: string | null
  benefitEndDate: string | null
  billingYear: number
  billingMonth: string
  planCost: PlanCostDto
  employer: number
  employee: number
  employeePayrollDeductionPreTax: number
  employeePayrollDeductionPostTax: number
  payrollCycle: number | null
  creditReason: string | null
  notes: string | null
  createdAt: string
}

export type FundingInvoiceYtdLineType = 'CHARGE' | 'CREDIT'

/**
 * A line on the Adjustments tab: every credit, every charge for a month other
 * than the funded month, and any funded-month charge that adjusts a prior. The
 * Health Plans shape plus the charge/credit type.
 */
export interface AdjustmentLineDto extends HealthPlanLineDto {
  type: FundingInvoiceYtdLineType
}

export interface YtdLineDto {
  lineItemId: string
  month: string
  billingYear: number
  billingMonth: string
  type: FundingInvoiceYtdLineType
  creditReason: string | null
  employeePublicKey: string
  employeeId: string | null
  employeeName: string
  firstName: string | null
  lastName: string | null
  relationship: string | null
  companyName: string | null
  companyCode: string | null
  divisionCode: string | null
  department: string | null
  departmentCode: string | null
  employmentStatus: string | null
  carrierName: string | null
  benefitId: string
  benefitEffectiveDate: string | null
  benefitEndDate: string | null
  planCost: PlanCostDto
  employer: number
  employee: number
  employeePayrollDeductionPreTax: number
  employeePayrollDeductionPostTax: number
  payrollCycle: number | null
  notes: string | null
  createdAt: string
}

/**
 * One Medicare reimbursement row: a funded-month snapshot of an employee's
 * Medicare ICHRA reimbursement (no premium, no corrections). All money in
 * dollars.
 */
export interface MedicareLineDto {
  lineItemId: string
  employeePublicKey: string
  employeeId: string | null
  employeeName: string
  firstName: string | null
  lastName: string | null
  relationship: string | null
  companyName: string | null
  companyCode: string | null
  divisionCode: string | null
  department: string | null
  departmentCode: string | null
  carrierName: string | null
  benefitId: string
  benefitEffectiveDate: string | null
  benefitEndDate: string | null
  billingYear: number
  billingMonth: string
  approvedMonthlyReimbursement: number
  employerMonthlyContribution: number
  employeePayrollPreTaxReimbursement: number
  employeePayrollPostTaxReimbursement: number
  payrollCycle: number | null
  notes: string | null
  createdAt: string
}

/**
 * One eligible-employee census row captured for the invoice at generate time
 * (read back from the invoice's census snapshot), rendered by the shared
 * CensusTable.
 */
export interface InvoiceCensusRecordDto {
  employeePublicKey?: string
  employeeId?: string
  firstName?: string
  lastName?: string
  department?: string
  benefitsEligible?: boolean
  hireDate?: string | null
  terminationDate?: string | null
  employedInMonth: boolean
}

/**
 * Calculated-fee inputs and computed totals captured for the invoice at generate
 * time (read back from the invoice's stored columns), rendered by
 * CalculatedFeesSummary.
 */
export interface InvoiceFeesDto {
  brokerName: string | null
  billingStructure: string | null
  surecoPepmCents: number | null
  brokerPepmCents: number | null
  tcgPepmCents: number | null
  minimumBillableEes: number | null
  enrollmentCount: number
  eligibleEmployeeCount: number
  billableEmployeeCount: number
  surecoFeeCents: number
  brokerFeeCents: number
  tcgFeeCents: number
}

export interface FundingInvoiceDetailDto {
  summary: InvoiceSummaryDto
  /** The funded month's premium lines only (line_category MONTHLY_PREMIUM). */
  healthPlans: HealthPlanLineDto[]
  /**
   * Credits, other-month lines and funded-month corrections (line_category
   * ADJUSTMENT), chronological. Absent from responses that predate line
   * categories, in which case `healthPlans` still carries every line and the
   * drawer splits it by the funded month.
   */
  adjustments?: AdjustmentLineDto[]
  ytd: YtdLineDto[]
  medicare: MedicareLineDto[]
  census: InvoiceCensusRecordDto[]
  fees: InvoiceFeesDto | null
}

export interface FundingInvoiceVersionsResponseDto {
  clientId: string
  billingPeriodStart: string
  versions: FundingInvoiceListItemDto[]
}

export interface GenerateInvoiceDto {
  clientPublicKey: string
  billingPeriodStart: string
}

export interface GenerateAllInvoicesDto {
  billingPeriodStart: string
}

export enum GenerateStatus {
  Created = 'created',
  Regenerated = 'regenerated',
  Failed = 'failed',
}

export interface GenerateResultDto {
  clientPublicKey: string
  status: GenerateStatus
  invoiceId?: string
  voidedInvoiceId?: string
  error?: string
  lineItemCount: number
  totalAmountCents: number
}

export interface GenerateAllResultDto {
  billingPeriodStart: string
  results: GenerateResultDto[]
}
